import { z } from 'zod';
import { PRICING_PLAN_TIERS, PricingPlanTierSchema } from './pricing-rules';
/**
 * Automatic VOLUME TIERS — the pure rule (volume-tier commission, 2026-10-04).
 *
 * A client's tier is earned from the number of deals that entered `paid` within
 * the rule set's rolling window (`TierRules`, pricing-rules.ts). Rank order is
 * `retail < dealer < dealer_pro`. Promotion is immediate; a tier that is no longer
 * earned is HELD for `holdDays` and then drops ONE step, starting a new hold if the
 * lower tier is still above what the client earns.
 *
 * Pure on purpose: the API's paid-transition hook, the daily sweep and the
 * read-only preview scripts all call these functions, so a tier can never be
 * computed two ways. Callers own the I/O (counting deals, persisting the result).
 */
const DAY_MS = 86_400_000;
/** Position of a tier in `retail < dealer < dealer_pro`. */
const rankOf = (tier) => PRICING_PLAN_TIERS.indexOf(tier);
/** The tier one step below `tier` (retail stays retail). */
function stepDown(tier) {
    return PRICING_PLAN_TIERS[Math.max(0, rankOf(tier) - 1)] ?? 'retail';
}
/** The tier `paidCount` paid cars in the window earns under `rules`. */
export function earnedTier(paidCount, rules) {
    if (paidCount >= rules.thresholds.dealer_pro)
        return 'dealer_pro';
    if (paidCount >= rules.thresholds.dealer)
        return 'dealer';
    return 'retail';
}
/**
 * The tier a client should hold at `now`.
 *
 *  - earned ≥ current → the earned tier, any hold cleared (`promoted` when it
 *    went up; `unchanged` when it stayed — the caller still persists a cleared
 *    hold, so it compares `holdUntil` as well as `tier`).
 *  - earned < current, no hold → keep the tier, hold until `now + holdDays`.
 *  - earned < current, hold running (`now < holdUntil`) → nothing changes.
 *  - earned < current, hold expired → drop ONE step; if that step is still above
 *    the earned tier a new hold starts from `now`, otherwise the hold clears.
 */
export function resolveVolumeTier(input) {
    const { current, holdUntil, paidCountInWindow, now, rules } = input;
    const earned = earnedTier(paidCountInWindow, rules);
    const holdEnd = new Date(now.getTime() + rules.holdDays * DAY_MS);
    if (rankOf(earned) >= rankOf(current)) {
        return {
            tier: earned,
            holdUntil: null,
            change: rankOf(earned) > rankOf(current) ? 'promoted' : 'unchanged',
        };
    }
    if (holdUntil === null)
        return { tier: current, holdUntil: holdEnd, change: 'hold_started' };
    if (now.getTime() < holdUntil.getTime())
        return { tier: current, holdUntil, change: 'unchanged' };
    const lower = stepDown(current);
    return {
        tier: lower,
        holdUntil: rankOf(lower) > rankOf(earned) ? holdEnd : null,
        change: 'demoted',
    };
}
/**
 * A client's progress through the volume tiers — `GET /api/deals/tier-progress`
 * and the admin client card.
 *
 * `active: false` while the rule set in force carries `tierRules: null`; the
 * window, next-tier and saving fields are then `null`. `nextTier` /
 * `carsToNextTier` / `savingsPerCarEur` are `null` on the top tier.
 */
export const TierProgressSchema = z.object({
    active: z.boolean(),
    tier: PricingPlanTierSchema,
    paidCountInWindow: z.number().int().min(0),
    windowDays: z.number().int().min(1).nullable(),
    nextTier: PricingPlanTierSchema.nullable(),
    carsToNextTier: z.number().int().min(0).nullable(),
    holdUntil: z.coerce.date().nullable(),
    /** Estimated commission saved per car on the next tier, EUR (never negative). */
    savingsPerCarEur: z.number().min(0).nullable(),
});
//# sourceMappingURL=volume-tier.js.map